import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Header, Footer } from '../components/Layout';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Mail, MessageCircle, Send, CheckCircle2, MapPin, Clock } from 'lucide-react';

const SUBJECTS = [
  'General inquiry',
  'Account / Login help',
  'Bet issue or dispute',
  'Wallet / Withdrawal',
  'KYC verification',
  'Business / Partnership',
  'Other',
];

const ContactUsPage = () => {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState(SUBJECTS[0]);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!fullName.trim() || !email.trim() || !message.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSending(true);
    try {
      await api.post('/contact/submit', {
        full_name: fullName, email, subject, message,
      });
      setSent(true);
      toast.success('Message sent — we will get back to you shortly');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#1A1A1A] flex flex-col">
      <Header />
      <main className="flex-1 max-w-5xl mx-auto px-4 py-8 w-full" data-testid="contact-us-page">
        <Link to="/" className="flex items-center gap-2 text-gray-400 hover:text-white mb-6 text-sm">
          <ArrowLeft className="h-4 w-4" /> Back to Home
        </Link>
        <h1 className="text-3xl font-black uppercase text-white mb-2" style={{ fontFamily: 'Outfit' }}>
          Contact <span className="text-[#D4AF37]">Us</span>
        </h1>
        <p className="text-gray-400 mb-8 text-sm">We usually reply within an hour. All messages go to <span className="text-[#D4AF37]">info@krazybets.online</span>.</p>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Contact Info */}
          <div className="lg:col-span-1 space-y-4">
            <div className="card-dark">
              <Mail className="h-6 w-6 text-[#D4AF37] mb-2" />
              <h3 className="text-white font-bold">Email Us</h3>
              <a href="mailto:info@krazybets.online" className="text-[#D4AF37] hover:underline text-sm">info@krazybets.online</a>
            </div>
            <div className="card-dark">
              <Clock className="h-6 w-6 text-[#D4AF37] mb-2" />
              <h3 className="text-white font-bold">Response Time</h3>
              <p className="text-sm text-gray-400">Under 1 hour, 24/7. Weekends included.</p>
            </div>
            <div className="card-dark">
              <MapPin className="h-6 w-6 text-[#D4AF37] mb-2" />
              <h3 className="text-white font-bold">Headquarters</h3>
              <p className="text-sm text-gray-400">Qfinity Media Group<br/>Cape Town, South Africa</p>
            </div>
            <div className="card-dark">
              <MessageCircle className="h-6 w-6 text-[#D4AF37] mb-2" />
              <h3 className="text-white font-bold">Prefer live chat?</h3>
              <p className="text-sm text-gray-400">Tap the gold chat bubble at the bottom-right to speak with our AI agent instantly.</p>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-2">
            <div className="card-dark">
              {sent ? (
                <div className="text-center py-8" data-testid="contact-success">
                  <div className="w-16 h-16 mx-auto rounded-full bg-[#28A745]/20 flex items-center justify-center mb-4">
                    <CheckCircle2 className="h-8 w-8 text-[#28A745]" />
                  </div>
                  <h2 className="text-2xl font-black text-white mb-2" style={{ fontFamily: 'Outfit' }}>Message received</h2>
                  <p className="text-gray-400 mb-6 text-sm">Thanks, {fullName.split(' ')[0] || 'friend'}. A copy was sent to info@krazybets.online and our support team will reply to <span className="text-[#D4AF37]">{email}</span> shortly.</p>
                  <Button onClick={() => { setSent(false); setFullName(''); setEmail(''); setMessage(''); setSubject(SUBJECTS[0]); }} className="btn-primary">Send another message</Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="font-semibold text-white text-sm">Full Name</Label>
                      <Input value={fullName} onChange={(e) => setFullName(e.target.value)} className="input-dark mt-1" placeholder="John Doe" required data-testid="contact-name" />
                    </div>
                    <div>
                      <Label className="font-semibold text-white text-sm">Email</Label>
                      <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="input-dark mt-1" placeholder="you@example.com" required data-testid="contact-email" />
                    </div>
                  </div>
                  <div>
                    <Label className="font-semibold text-white text-sm">Subject</Label>
                    <select value={subject} onChange={(e) => setSubject(e.target.value)} className="input-dark mt-1 w-full" data-testid="contact-subject">
                      {SUBJECTS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="font-semibold text-white text-sm">Message</Label>
                    <textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      rows={6}
                      className="input-dark mt-1 w-full resize-none"
                      placeholder="Tell us what's going on..."
                      required
                      data-testid="contact-message"
                    />
                  </div>
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <p className="text-xs text-gray-500">By submitting, you agree to our <Link to="/privacy" className="text-[#D4AF37] hover:underline">Privacy Policy</Link>.</p>
                    <Button type="submit" className="btn-primary" disabled={sending} data-testid="contact-submit">
                      <Send className="h-4 w-4 mr-2" /> {sending ? 'Sending...' : 'Send Message'}
                    </Button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ContactUsPage;
